import { useEffect, useRef, useState } from 'react';
import { useDialogFocus } from "../../../../hooks/useDialogFocus";
import {
  resetRosterSettings,
  updateRosterSettings,
  updateScholarshipConfiguration,
} from '../../../../services/scholarships';
import {
  SCHOLARSHIP_PLAYER_POSITIONS,
  type PositionBudget,
  type RosterSettings,
  type Scholarship,
  type ScholarshipModel,
  type ScholarshipPlayerPosition,
} from '../../../../types/scholarships';
import { TextInput } from '../../../../components/TextInput';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/Checkbox';
import { ModalCloseButton } from '../../../../components/ModalCloseButton';
import { pitchShapeLabel, rosterSettingsOf, sportHasArmAngle } from './rosterPalette';
import {
  type OptionListKey,
  rosterSettingsChanged,
  trimRosterSettings,
  validateRosterSettings,
} from './rosterSettingsDraft';
import {
  EvaluationTraitsEditor,
  IndicatorsEditor,
  OptionChipsEditor,
} from './RosterSettingsEditors';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';
import { MoneyInput } from './MoneyInput';
import { positionLabel, type RosterSport } from './positionGroups';
import { numberToMoneyString, parseMoneyString } from './money';
import { formatMoney } from './scholarshipMoney';
import { configureBoardLabel, isRecruitingBoard } from './boardPurpose';
import './ConfigureLimitsModal.css';

interface ConfigureLimitsModalProps {
  scholarship: Scholarship;
  onClose: () => void;
  onSuccess: () => void;
  /** Passed rather than read from context so this stays renderable without an AuthProvider. */
  sport: RosterSport;
}

interface ModelCard {
  model: ScholarshipModel;
  title: string;
  subtitle: string;
}

const MODEL_CARDS: ModelCard[] = [
  {
    model: 'FullHalf',
    title: 'Full / Half Scholarship Model',
    subtitle: 'Players are assigned a Full (1.0) or Half (0.5) scholarship',
  },
  {
    model: 'Equivalency',
    title: 'Equivalency Model',
    subtitle: 'Set a total number of scholarships; assign each player a percentage',
  },
  {
    // API value stays 'Percentage' — only the display name is "Dollar Budget Model".
    model: 'Percentage',
    title: 'Dollar Budget Model',
    subtitle: 'Set total dollar budgets and assign actual dollar amounts',
  },
];

/** Number → display string for plain numeric inputs ('' when null). */
const numberToInputString = (n: number | null): string => (n == null ? '' : n.toString());

/** Parses a free-typed numeric string; '' → null, garbage → null. */
const parseNumberString = (s: string): number | null => {
  const trimmed = s.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
};

type PositionBudgetDraft = { units: string; dollars: string; needed: string; note: string };

/** The dollars spread across positions so far: every row's budget that parses, summed. */
const allocatedDollars = (budgets: Record<string, PositionBudgetDraft>): number =>
  Object.values(budgets).reduce((sum, entry) => {
    const dollars = parseMoneyString(entry.dollars);
    return dollars !== null && dollars > 0 ? sum + dollars : sum;
  }, 0);

/** The scholarships spread across positions so far, for the Full / Half and Equivalency models. */
const allocatedUnits = (budgets: Record<string, PositionBudgetDraft>): number =>
  Object.values(budgets).reduce((sum, entry) => {
    const units = parseNumberString(entry.units);
    return units !== null && units > 0 ? sum + units : sum;
  }, 0);

/** Players needed across every position: each row's count that is a whole number, summed. */
const totalNeeded = (budgets: Record<string, PositionBudgetDraft>): number =>
  Object.values(budgets).reduce((sum, entry) => {
    const needed = parseNumberString(entry.needed);
    return needed !== null && needed > 0 && Number.isInteger(needed) ? sum + needed : sum;
  }, 0);

/** "4.5 scholarships", "1 scholarship". Two decimals at most, so 0.1 + 0.2 never reads 0.30000000004. */
const formatUnits = (n: number): string => {
  const rounded = Math.round(n * 100) / 100;
  return `${rounded.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${
    rounded === 1 ? 'scholarship' : 'scholarships'
  }`;
};

/**
 * The running total under the position table: what the rows add up to, against the board's budget
 * when it has one, and (on a recruiting board) how many players the rows ask for. Plain text rather
 * than a live region: it changes on every keystroke, and announcing each one would talk over the
 * field being typed in. It sits right under the table.
 */
const PositionAllocationTotal = ({
  allocated,
  budget,
  format,
  needed,
}: {
  allocated: number;
  budget: number | null;
  format: (n: number) => string;
  /** Null leaves the players-needed line out. */
  needed: number | null;
}) => {
  // Compared in cents (or hundredths of a scholarship), so float noise never reads as "over".
  const allocatedHundredths = Math.round(allocated * 100);
  const budgetHundredths = budget === null ? null : Math.round(budget * 100);
  const over = budgetHundredths !== null && allocatedHundredths > budgetHundredths;
  // From the rounded figures too, so a float remainder never prints as "-0" or "0.0000001".
  const difference = budgetHundredths === null ? 0 : Math.abs(budgetHundredths - allocatedHundredths) / 100;
  return (
    <div>
      <p
        className={over ? 'configure-limits__allocation configure-limits__allocation--over' : 'configure-limits__allocation'}
        data-testid="position-allocation-total"
      >
        <span className="configure-limits__allocation-label">Allocated:</span>{' '}
        <strong>{format(allocated)}</strong>
        {budget === null
          ? ' (no total budget set)'
          : ` of ${format(budget)}, ${
              over
                ? `${format(difference)} over budget`
                : `${format(difference)} left`
            }`}
      </p>
      {needed !== null && (
        <p className="configure-limits__allocation" data-testid="position-needed-total">
          <span className="configure-limits__allocation-label">Players needed:</span>{' '}
          <strong>{needed}</strong>
        </p>
      )}
    </div>
  );
};

export const ConfigureLimitsModal = ({
  scholarship,
  onClose,
  onSuccess,
  sport,
}: ConfigureLimitsModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ isOpen: true, dialogRef, handleEscape: false });

  /*
    A recruiting board plans one class, so its dialog is "Configure board", its budgets read "for
    this class", and it drops the roster-only fields: target average and roster spots. Those values
    stay in state and are re-sent on Save exactly as loaded, so hiding them never clears them.
  */
  const recruiting = isRecruitingBoard(scholarship);
  const dialogTitle = configureBoardLabel(scholarship);
  const budgetPhrase = recruiting ? 'Scholarship budget for this class' : 'Scholarship budget';
  const pitchingTabName = recruiting ? 'Pitchers' : 'Pitching staff';

  const [model, setModel] = useState<ScholarshipModel>(scholarship.scholarshipModel);
  // Keep BOTH budget values in state regardless of which model is active, so
  // switching models never erases the hidden budget. Both are always sent on save.
  const [budgetUnits, setBudgetUnits] = useState(
    numberToInputString(scholarship.budgetUnits),
  );
  const [budgetDollars, setBudgetDollars] = useState(
    numberToMoneyString(scholarship.budgetDollars),
  );
  const [rosterLimit, setRosterLimit] = useState(
    numberToInputString(scholarship.rosterLimit),
  );

  // Optional target AVERAGE per funded player. Like the budget values, both unit and
  // dollar targets are kept in state and always sent, so switching model never wipes them.
  const [targetAverageUnits, setTargetAverageUnits] = useState(
    numberToInputString(scholarship.targetAverageUnits),
  );
  const [targetAverageDollars, setTargetAverageDollars] = useState(
    numberToMoneyString(scholarship.targetAverageDollars),
  );

  // Per-position budgets. Both unit + dollar strings are held per position (keep-but-ignore);
  // only the active model's field is shown, the other is preserved and re-sent on save.
  const [positionBudgets, setPositionBudgets] = useState<
    Record<string, PositionBudgetDraft>
  >(() => {
    const map: Record<string, PositionBudgetDraft> = {};
    for (const pos of SCHOLARSHIP_PLAYER_POSITIONS) {
      map[pos] = { units: '', dollars: '', needed: '', note: '' };
    }
    for (const pb of scholarship.positionBudgets) {
      map[pb.position] = {
        units: numberToInputString(pb.budgetUnits),
        dollars: numberToMoneyString(pb.budgetDollars),
        needed: pb.playersNeeded == null ? '' : String(pb.playersNeeded),
        note: pb.note ?? '',
      };
    }
    return map;
  });

  const setPositionBudgetField = (
    pos: ScholarshipPlayerPosition,
    field: 'units' | 'dollars' | 'needed' | 'note',
    value: string,
  ) =>
    setPositionBudgets((prev) => ({
      ...prev,
      [pos]: { ...prev[pos], [field]: value },
    }));

  // Keep-but-ignore: budget values stay in state (and are always sent) even while their
  // tracking toggle is off, so re-enabling tracking restores the prior budget.
  const [nilTrackingEnabled, setNilTrackingEnabled] = useState(
    scholarship.nilTrackingEnabled,
  );
  const [nilBudget, setNilBudget] = useState(numberToMoneyString(scholarship.nilBudget));
  const [revShareTrackingEnabled, setRevShareTrackingEnabled] = useState(
    scholarship.revShareTrackingEnabled,
  );
  const [revShareBudget, setRevShareBudget] = useState(
    numberToMoneyString(scholarship.revShareBudget),
  );

  // The board's option lists, edited as one draft and saved after the configuration. `savedSettings`
  // is what the server holds now, so an untouched draft is not written back (see
  // rosterSettingsChanged) and Restore defaults can move it.
  const [savedSettings, setSavedSettings] = useState<RosterSettings>(() =>
    rosterSettingsOf(scholarship),
  );
  const [settings, setSettings] = useState<RosterSettings>(savedSettings);
  const [customized, setCustomized] = useState(scholarship.rosterSettingsCustomized ?? false);
  // Restore defaults writes at once. After it, the board behind the modal is stale, so closing
  // without saving still has to refresh it.
  const [boardChanged, setBoardChanged] = useState(false);
  const [confirmingRestore, setConfirmingRestore] = useState(false);
  /*
    Focus for the inline confirm. Opening it disables the Restore button that had focus, so focus
    moves into the confirm; closing it (either answer) puts focus back on Restore, or on Save when
    Restore has nothing left to do and is disabled. A disabled button cannot hold focus, and
    leaving it there drops a keyboard user to the top of the page.
  */
  const restoreButtonRef = useRef<HTMLButtonElement>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const keepListsRef = useRef<HTMLButtonElement>(null);
  const returnFocusFromConfirm = useRef(false);

  const setList = (key: OptionListKey) =>
    (values: string[]) => setSettings((prev) => ({ ...prev, [key]: values }));

  const [fieldError, setFieldError] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const close = boardChanged ? onSuccess : onClose;

  // True only when the press that began the current click landed on the overlay
  // itself — so drag-selecting a value and releasing over the backdrop doesn't
  // close the modal mid-selection.
  const pressedOnOverlay = useRef(false);

  useEffect(() => {
    const trigger = (document.activeElement as HTMLElement) ?? null;
    return () => {
      trigger?.focus?.();
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) {
        close();
      }
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isSaving, close]);

  // A roster board shows the position table on the Dollar Budget model only; a recruiting board on all.
  const showPositionTable = recruiting || model === 'Percentage';

  const handleSave = async () => {
    if (isSaving) return;
    const unitsValue = parseNumberString(budgetUnits);
    const dollarsValue = parseMoneyString(budgetDollars);
    const rosterValue = parseNumberString(rosterLimit);
    const nilBudgetValue = parseMoneyString(nilBudget);
    const revShareBudgetValue = parseMoneyString(revShareBudget);
    const targetUnitsValue = parseNumberString(targetAverageUnits);
    const targetDollarsValue = parseMoneyString(targetAverageDollars);

    if (unitsValue !== null && unitsValue < 0) {
      setFieldError('Scholarship budget must be zero or greater');
      return;
    }
    if (dollarsValue !== null && dollarsValue < 0) {
      setFieldError('Scholarship budget must be zero or greater');
      return;
    }
    if (rosterValue !== null && (rosterValue < 0 || !Number.isInteger(rosterValue))) {
      setFieldError('Roster spots must be a whole number, zero or greater');
      return;
    }
    if (nilBudgetValue !== null && nilBudgetValue < 0) {
      setFieldError('NIL budget must be zero or greater');
      return;
    }
    if (revShareBudgetValue !== null && revShareBudgetValue < 0) {
      setFieldError('Revenue Share budget must be zero or greater');
      return;
    }
    if (targetUnitsValue !== null && targetUnitsValue < 0) {
      setFieldError('Target average must be zero or greater');
      return;
    }
    if (targetDollarsValue !== null && targetDollarsValue < 0) {
      setFieldError('Target average must be zero or greater');
      return;
    }

    // Build the full-replace per-position budget set. A position with both fields blank is
    // omitted (clears its budget). Both unit + dollar values are sent (keep-but-ignore).
    const positionBudgetsPayload: PositionBudget[] = [];
    for (const pos of SCHOLARSHIP_PLAYER_POSITIONS) {
      const entry = positionBudgets[pos];
      const unitsShown = showPositionTable && model !== 'Percentage';
      // A hidden cell that does not parse (typed into, then hidden by a model switch) sends the
      // value it was loaded with rather than null, so the full-replace save never clears it.
      const loaded = scholarship.positionBudgets.find((pb) => pb.position === pos);
      const typedUnits = parseNumberString(entry.units);
      const units =
        typedUnits === null && !unitsShown && entry.units.trim() !== ''
          ? loaded?.budgetUnits ?? null
          : typedUnits;
      const dollars = parseMoneyString(entry.dollars);
      const typedNeeded = parseNumberString(entry.needed);
      const needed =
        typedNeeded === null && !showPositionTable && entry.needed.trim() !== ''
          ? loaded?.playersNeeded ?? null
          : typedNeeded;
      const note = entry.note.trim();
      // A row with nothing on it at all is omitted, which clears that position. Needs and
      // notes count as "something" — a coach can plan headcount without planning dollars.
      // Typed but not a number: refuse rather than save the row as if the field were blank. Only for
      // a field on screen right now, so Save never fails over a cell the coach cannot see (a units
      // cell left behind by a switch to the Dollar Budget model).
      if (
        (unitsShown && entry.units.trim() !== '' && typedUnits === null) ||
        (showPositionTable && entry.needed.trim() !== '' && typedNeeded === null)
      ) {
        setFieldError(`${positionLabel(pos, sport)} needs a number`);
        return;
      }
      if (units === null && dollars === null && needed === null && note.length === 0) continue;
      if ((units !== null && units < 0) || (dollars !== null && dollars < 0)) {
        setFieldError(`${positionLabel(pos, sport)} budget must be zero or greater`);
        return;
      }
      if (needed !== null && (needed < 0 || !Number.isInteger(needed))) {
        setFieldError(`${positionLabel(pos, sport)} players needed must be a whole number of zero or more`);
        return;
      }
      positionBudgetsPayload.push({
        position: pos,
        budgetUnits: units,
        budgetDollars: dollars,
        playersNeeded: needed,
        note: note.length > 0 ? note : null,
      });
    }

    const settingsProblem = validateRosterSettings(settings);
    if (settingsProblem) {
      setFieldError(settingsProblem);
      return;
    }

    setFieldError(undefined);
    // Save writes the lists too, so an open Restore confirm answered mid-save would race it.
    setConfirmingRestore(false);
    setIsSaving(true);
    setSaveError(null);

    let configurationSaved = false;
    try {
      // Always send ALL config values (full-replace endpoint) so the inactive model's
      // budget — and a disabled toggle's budget — are preserved, never wiped.
      await updateScholarshipConfiguration(scholarship.id, {
        scholarshipModel: model,
        budgetUnits: unitsValue,
        budgetDollars: dollarsValue,
        rosterLimit: rosterValue,
        targetAverageUnits: targetUnitsValue,
        targetAverageDollars: targetDollarsValue,
        nilTrackingEnabled,
        nilBudget: nilBudgetValue,
        revShareTrackingEnabled,
        revShareBudget: revShareBudgetValue,
        positionBudgets: positionBudgetsPayload,
      });
      configurationSaved = true;
      // Second, and only when something changed: writing an untouched draft would mark a board
      // that runs on the defaults as customised and freeze today's defaults onto it.
      if (rosterSettingsChanged(settings, savedSettings)) {
        await updateRosterSettings(scholarship.id, trimRosterSettings(settings));
      }
      onSuccess();
    } catch (error) {
      // The two writes are separate requests. If the first landed, say so, so a coach does not
      // think the budgets were lost too; Save again re-sends both, which is harmless.
      setBoardChanged((changed) => changed || configurationSaved);
      setSaveError(
        configurationSaved
          ? `Budgets and limits were saved, but the roster lists were not. ${getSafeErrorMessage(error, 'Failed to save roster lists')}`
          : getSafeErrorMessage(error, 'Failed to save configuration'),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleOverlayMouseDown = (e: React.MouseEvent) => {
    pressedOnOverlay.current = e.target === e.currentTarget;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    const isOverlayClick = e.target === e.currentTarget && pressedOnOverlay.current;
    pressedOnOverlay.current = false;
    if (isOverlayClick && !isSaving) {
      close();
    }
  };

  /*
    Restore defaults puts every roster list back to the API's defaults: traits, indicators, name
    colours and the pitching / type / status lists. It saves at once (it is a DELETE, not a draft
    edit), after an inline confirm, because it replaces lists a coach may have spent a while on.
    The budget fields above are not touched. On a board still on the defaults there is nothing to
    delete, so it only discards unsaved edits.
  */
  const closeRestoreConfirm = () => {
    returnFocusFromConfirm.current = true;
    setConfirmingRestore(false);
  };

  useEffect(() => {
    if (confirmingRestore) {
      keepListsRef.current?.focus();
      return;
    }
    if (!returnFocusFromConfirm.current || isSaving) return;
    returnFocusFromConfirm.current = false;
    const restore = restoreButtonRef.current;
    (restore && !restore.disabled ? restore : saveButtonRef.current)?.focus();
  }, [confirmingRestore, isSaving]);

  const handleRestoreDefaults = async () => {
    if (isSaving) return;
    closeRestoreConfirm();
    setSaveError(null);
    setFieldError(undefined);
    if (!customized) {
      setSettings(savedSettings);
      return;
    }
    setIsSaving(true);
    try {
      const board = await resetRosterSettings(scholarship.id);
      const defaults = rosterSettingsOf(board);
      setSavedSettings(defaults);
      setSettings(defaults);
      setCustomized(board.rosterSettingsCustomized ?? false);
      setBoardChanged(true);
    } catch (error) {
      setSaveError(getSafeErrorMessage(error, 'Failed to restore the default lists'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div ref={dialogRef} tabIndex={-1}
      className="scholarship-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="configure-limits-title"
      aria-describedby="configure-limits-description"
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div className="scholarship-modal configure-limits-modal">
        <div className="configure-limits__header">
          <div>
            <h2 id="configure-limits-title" className="scholarship-modal__title">
              {dialogTitle}
            </h2>
            <p id="configure-limits-description" className="configure-limits__hint">
              {recruiting
                ? 'Scholarship rules, budgets for this class, position needs, and the lists this board uses to describe players. Everything is optional.'
                : 'Scholarship rules, funding, roster size, position needs, and the lists this board uses to describe players. Everything is optional.'}
            </p>
          </div>
          <ModalCloseButton onClick={close} disabled={isSaving} />
        </div>
        <form
          className="scholarship-modal__form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          {/* Two columns on a wide screen, like the mockup: money and capacity with the traits on
              the left, needs and indicators on the right. One column below that. */}
          <div className="configure-limits__columns">
            <div className="configure-limits__column">
              <div
                className="configure-limits__group"
                role="radiogroup"
                aria-label="Scholarship model"
              >
                <p className="configure-limits__group-label">Scholarship Model</p>
                <div className="configure-limits__cards">
                  {MODEL_CARDS.map((card) => {
                    const isSelected = model === card.model;
                    return (
                      <button
                        key={card.model}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        data-testid={`scholarship-model-${card.model}`}
                        className={
                          isSelected
                            ? 'configure-limits__card configure-limits__card--selected'
                            : 'configure-limits__card'
                        }
                        onClick={() => setModel(card.model)}
                        disabled={isSaving}
                      >
                        <span className="configure-limits__card-title">{card.title}</span>
                        <span className="configure-limits__card-subtitle">
                          {card.subtitle}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {model === 'Percentage' ? (
                <div className="configure-limits__group">
                  <MoneyInput
                    label={`${budgetPhrase} ($ total)`}
                    value={budgetDollars}
                    onChange={setBudgetDollars}
                    disabled={isSaving}
                    placeholder="e.g. 250,000"
                  />
                  <p className="configure-limits__hint">Leave blank for unlimited.</p>
                </div>
              ) : (
                <div className="configure-limits__group">
                  <TextInput
                    label={`${budgetPhrase} (number of scholarships)`}
                    type="text"
                    inputMode="decimal"
                    data-testid="budget-units-input"
                    value={budgetUnits}
                    onChange={(e) => setBudgetUnits(e.target.value)}
                    disabled={isSaving}
                    placeholder="e.g. 12 or 12.5"
                  />
                  <p className="configure-limits__hint">Leave blank for unlimited.</p>
                </div>
              )}

              {recruiting ? null : model === 'Percentage' ? (
                <div className="configure-limits__group">
                  <MoneyInput
                    label="Target average scholarship ($ per player)"
                    value={targetAverageDollars}
                    onChange={setTargetAverageDollars}
                    disabled={isSaving}
                    placeholder="e.g. 8,000"
                  />
                  <p className="configure-limits__hint">
                    Leave blank for no target. The summary flags when the current average exceeds it.
                  </p>
                </div>
              ) : (
                <div className="configure-limits__group">
                  <TextInput
                    label={
                      model === 'Equivalency'
                        ? 'Target average scholarship (equivalency per player)'
                        : 'Target average scholarship (units per player)'
                    }
                    type="text"
                    inputMode="decimal"
                    data-testid="target-average-units-input"
                    value={targetAverageUnits}
                    onChange={(e) => setTargetAverageUnits(e.target.value)}
                    disabled={isSaving}
                    placeholder="e.g. 0.5"
                  />
                  <p className="configure-limits__hint">
                    Leave blank for no target. The summary flags when the current average exceeds it.
                  </p>
                </div>
              )}

              <div className="configure-limits__tracking">
                <div className="configure-limits__group">
                  <Checkbox
                    label="Enable NIL tracking"
                    data-testid="nil-tracking-checkbox"
                    checked={nilTrackingEnabled}
                    onChange={(e) => setNilTrackingEnabled(e.target.checked)}
                    disabled={isSaving}
                  />
                  <MoneyInput
                    label={recruiting ? 'NIL budget for this class' : 'NIL budget'}
                    value={nilBudget}
                    onChange={setNilBudget}
                    disabled={isSaving || !nilTrackingEnabled}
                    placeholder="e.g. 100,000"
                  />
                  <p className="configure-limits__hint">Leave blank for unlimited.</p>
                </div>

                <div className="configure-limits__group">
                  <Checkbox
                    label="Enable Revenue Share tracking"
                    data-testid="rev-share-tracking-checkbox"
                    checked={revShareTrackingEnabled}
                    onChange={(e) => setRevShareTrackingEnabled(e.target.checked)}
                    disabled={isSaving}
                  />
                  <MoneyInput
                    label="Revenue Share budget"
                    value={revShareBudget}
                    onChange={setRevShareBudget}
                    disabled={isSaving || !revShareTrackingEnabled}
                    placeholder="e.g. 50,000"
                  />
                  <p className="configure-limits__hint">Leave blank for unlimited.</p>
                </div>
              </div>

              {!recruiting && (
                <div className="configure-limits__group">
                  <TextInput
                    label="Roster spots allowed"
                    type="text"
                    inputMode="numeric"
                    data-testid="roster-limit-input"
                    value={rosterLimit}
                    onChange={(e) => setRosterLimit(e.target.value)}
                    disabled={isSaving}
                    placeholder="e.g. 35"
                  />
                  <p className="configure-limits__hint">Leave blank for unlimited.</p>
                </div>
              )}

              <EvaluationTraitsEditor settings={settings} onChange={setSettings} disabled={isSaving} />
            </div>
            <div className="configure-limits__column">
              {/* A roster board shows this on the Dollar Budget model only: coaches on Equivalency or
                  Full / Half found a dollar-shaped table on a scholarship-count board confusing, and
                  asked for it to go. A recruiting board shows it on every model (10/4 feedback), with
                  the budget column in the model's own unit. Whatever is hidden stays in state and is
                  re-sent on Save, never wiped. */}
              {showPositionTable && (
                <div className="configure-limits__group">
                  <p id="configure-needs-title" className="configure-limits__group-label">Position Needs &amp; Budgets</p>
                  <p className="configure-limits__hint">
                    Per position: an optional cap ({model === 'Percentage' ? 'dollars' : 'scholarships'}),
                    how many players you want, and a note. All optional. Needs drive the Position
                    Needs and Recruiting Progress cards.
                  </p>
                  {/* One header row and one line per position, like a table. Every field used to carry
                      its own label, so twelve positions stacked twelve "Needed / Note" captions. */}
                  <div className="configure-limits__position-table" role="group" aria-labelledby="configure-needs-title">
                    <div className="configure-limits__position-head" aria-hidden="true">
                      <span>Position</span>
                      <span>{model === 'Percentage' ? 'Budget ($)' : 'Budget (scholarships)'}</span>
                      <span>Needed</span>
                      <span>Note</span>
                    </div>
                    {SCHOLARSHIP_PLAYER_POSITIONS.map((pos) => {
                      const name = positionLabel(pos, sport);
                      return (
                        <div className="configure-limits__position-row" key={pos}>
                          <span className="configure-limits__position-name">{name}</span>
                          {model === 'Percentage' ? (
                            <MoneyInput
                              ariaLabel={`${name} budget`}
                              value={positionBudgets[pos].dollars}
                              onChange={(v) => setPositionBudgetField(pos, 'dollars', v)}
                              disabled={isSaving}
                              placeholder="—"
                            />
                          ) : (
                            <TextInput
                              aria-label={`${name} budget (scholarships)`}
                              type="text"
                              inputMode="decimal"
                              value={positionBudgets[pos].units}
                              onChange={(e) => setPositionBudgetField(pos, 'units', e.target.value)}
                              disabled={isSaving}
                              placeholder="—"
                            />
                          )}
                          <TextInput
                            aria-label={`${name} players needed`}
                            type="text"
                            inputMode="numeric"
                            value={positionBudgets[pos].needed}
                            onChange={(e) => setPositionBudgetField(pos, 'needed', e.target.value)}
                            disabled={isSaving}
                            placeholder="—"
                          />
                          <TextInput
                            aria-label={`${name} note`}
                            value={positionBudgets[pos].note}
                            onChange={(e) => setPositionBudgetField(pos, 'note', e.target.value)}
                            disabled={isSaving}
                            maxLength={500}
                            placeholder="Optional"
                          />
                        </div>
                      );
                    })}
                  </div>
                  {model === 'Percentage' ? (
                    <PositionAllocationTotal
                      allocated={allocatedDollars(positionBudgets)}
                      budget={parseMoneyString(budgetDollars)}
                      format={formatMoney}
                      needed={recruiting ? totalNeeded(positionBudgets) : null}
                    />
                  ) : (
                    <PositionAllocationTotal
                      allocated={allocatedUnits(positionBudgets)}
                      budget={parseNumberString(budgetUnits)}
                      format={formatUnits}
                      needed={recruiting ? totalNeeded(positionBudgets) : null}
                    />
                  )}
                </div>
              )}

              <IndicatorsEditor settings={settings} onChange={setSettings} disabled={isSaving} />
            </div>
          </div>

          <section className="settings-section" aria-labelledby="configure-pitching-lists">
            <h3 id="configure-pitching-lists" className="configure-limits__group-label">
              {pitchingTabName}
            </h3>
            <p className="configure-limits__hint">
              The choices offered on the {pitchingTabName} tab and on a pitcher's profile.
            </p>
            <div className="settings-chips-grid">
              {/* Softball pitchers all throw from one slot, so there is no list to keep. */}
              {sportHasArmAngle(sport) && (
                <OptionChipsEditor
                  title="Arm angles"
                  noun="arm angle"
                  values={settings.armAngles}
                  onChange={setList('armAngles')}
                  disabled={isSaving}
                />
              )}
              <OptionChipsEditor
                title="Pitch shapes"
                noun="pitch shape"
                hint="Short codes, like SK or SL. The staff table shows them joined, SK/SL."
                values={settings.pitchShapes}
                onChange={setList('pitchShapes')}
                labelFor={pitchShapeLabel}
                disabled={isSaving}
              />
              <OptionChipsEditor
                title="Pitching roles"
                noun="pitching role"
                values={settings.pitchingRoles}
                onChange={setList('pitchingRoles')}
                disabled={isSaving}
              />
            </div>
          </section>

          {fieldError && (
            <div className="scholarship-modal__error" role="alert">{fieldError}</div>
          )}
          {saveError && (
            <div className="scholarship-modal__error" role="alert">{saveError}</div>
          )}

          {confirmingRestore && (
            <div className="configure-limits__confirm" role="group" aria-labelledby="restore-defaults-prompt">
              <p id="restore-defaults-prompt" className="configure-limits__confirm-text">
                Put every list back to the defaults? This replaces your evaluation tags, indicators,
                name colors and option lists right away. Budgets and position needs stay as they are.
              </p>
              <div className="configure-limits__confirm-actions">
                <Button
                  ref={keepListsRef}
                  variant="secondary"
                  type="button"
                  onClick={closeRestoreConfirm}
                  disabled={isSaving}
                >
                  Keep my lists
                </Button>
                <Button
                  variant="primary"
                  type="button"
                  onClick={handleRestoreDefaults}
                  disabled={isSaving}
                >
                  Restore defaults
                </Button>
              </div>
            </div>
          )}

          <div className="scholarship-modal__actions configure-limits__actions">
            <Button
              ref={restoreButtonRef}
              variant="secondary"
              type="button"
              className="configure-limits__restore"
              onClick={() => setConfirmingRestore(true)}
              disabled={
                isSaving ||
                confirmingRestore ||
                (!customized && !rosterSettingsChanged(settings, savedSettings))
              }
              data-testid="configure-restore-defaults"
            >
              Restore default lists
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={close}
              disabled={isSaving}
              data-testid="configure-limits-cancel"
            >
              Cancel
            </Button>
            <Button
              ref={saveButtonRef}
              variant="primary"
              type="submit"
              disabled={isSaving}
              data-testid="configure-limits-save"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
